import React, { useState, useRef } from 'react';
import { Session } from '../types';
import { recordSwipe, markUserFinished } from '../sessionService';
import { getPriceLevelString, getCuisineEmoji, formatRating } from '../utils';

interface SwipeScreenProps {
  session: Session;
  sessionCode: string;
  user: 'userA' | 'userB';
  userName: string;
  onFinished: () => void;
}

const SwipeScreen: React.FC<SwipeScreenProps> = ({ session, sessionCode, user, userName, onFinished }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [flyingOut, setFlyingOut] = useState<'yes' | 'no' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const startXRef = useRef(0);
  const restaurants = session.restaurants;
  const current = restaurants[currentIndex];
  const progress = currentIndex / restaurants.length;

  const handleSwipe = async (direction: 'yes' | 'no') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setFlyingOut(direction);

    await recordSwipe(sessionCode, user, current.id, direction);

    setTimeout(async () => {
      if (currentIndex + 1 >= restaurants.length) {
        await markUserFinished(sessionCode, user);
        onFinished();
      } else {
        setCurrentIndex(i => i + 1);
        setDragX(0);
        setFlyingOut(null);
        setIsAnimating(false);
      }
    }, 400);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isAnimating) return;
    startXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || isAnimating) return;
    setDragX(e.touches[0].clientX - startXRef.current);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragX > 80) handleSwipe('yes');
    else if (dragX < -80) handleSwipe('no');
    else setDragX(0);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isAnimating) return;
    startXRef.current = e.clientX;
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || isAnimating) return;
    setDragX(e.clientX - startXRef.current);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragX > 80) handleSwipe('yes');
    else if (dragX < -80) handleSwipe('no');
    else setDragX(0);
  };

  const getCardStyle = (): React.CSSProperties => {
    if (flyingOut === 'yes') {
      return {
        transform: `translateX(120vw) rotate(30deg)`,
        transition: 'transform 0.4s ease-out',
      };
    }
    if (flyingOut === 'no') {
      return {
        transform: `translateX(-120vw) rotate(-30deg)`,
        transition: 'transform 0.4s ease-out',
      };
    }
    const rotation = dragX * 0.08;
    return {
      transform: `translateX(${dragX}px) rotate(${rotation}deg)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease',
    };
  };

  const yesOpacity = flyingOut === 'yes' ? 1 : Math.min(Math.max(dragX / 100, 0), 1);
  const noOpacity = flyingOut === 'no' ? 1 : Math.min(Math.max(-dragX / 100, 0), 1);

  if (!current) return null;

  return (
    <div className="flex flex-col bg-dinder-dark select-none" style={{ height: '100dvh' }}>

      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-8 pb-3 flex-shrink-0">
        <div>
          <h2 className="text-white font-bold text-lg">🍽️ Dinder</h2>
          <p className="text-blue-200 text-xs">Swiping as {userName}</p>
        </div>
        <div className="text-right">
          <p className="text-white text-sm font-semibold">{currentIndex + 1} of {restaurants.length}</p>
          <p className="text-blue-200 text-xs">swipe generously!</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mx-5 h-1 bg-white/20 rounded-full mb-4 flex-shrink-0">
        <div
          className="h-1 bg-dinder-pink rounded-full transition-all duration-300"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* Card area */}
      <div className="flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div
          className="swipe-card relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl cursor-grab active:cursor-grabbing"
          style={{
            ...getCardStyle(),
            height: '62vh',
            maxHeight: '500px',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {/* Photo */}
          {current.photoUrl ? (
            <img
              src={current.photoUrl}
              alt={current.name}
              className="w-full h-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-dinder-card flex items-center justify-center">
              <span className="text-8xl">{getCuisineEmoji(current.cuisine)}</span>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

          {/* YES stamp */}
          <div
            className="absolute top-8 left-6 border-4 border-green-400 rounded-xl px-4 py-2 rotate-[-20deg] transition-opacity duration-100"
            style={{ opacity: yesOpacity }}
          >
            <p className="text-green-400 font-extrabold text-3xl">YES!</p>
          </div>

          {/* NOPE stamp */}
          <div
            className="absolute top-8 right-6 border-4 border-red-400 rounded-xl px-4 py-2 rotate-[20deg] transition-opacity duration-100"
            style={{ opacity: noOpacity }}
          >
            <p className="text-red-400 font-extrabold text-3xl">NOPE</p>
          </div>

          {/* Restaurant info */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-white font-extrabold text-2xl leading-tight flex-1 pr-2">{current.name}</h3>
              <span className="text-2xl">{getCuisineEmoji(current.cuisine)}</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-yellow-400 font-bold">★ {formatRating(current.rating)}</span>
              <span className="text-gray-300 text-sm">({current.reviewCount.toLocaleString()} reviews)</span>
              <span className="text-gray-300 text-sm">{getPriceLevelString(current.priceLevel)}</span>
            </div>
            <p className="text-gray-300 text-sm mb-2">{current.address}</p>
            {current.summary ? (
              <p className="text-gray-300 text-xs italic leading-relaxed line-clamp-2">"{current.summary}"</p>
            ) : null}
            {!current.isOpen && (
              <span className="inline-block mt-2 bg-red-900 text-red-300 text-xs px-2 py-1 rounded-full">
                Currently closed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-center gap-8 py-6 px-6 flex-shrink-0">
        <button
          onClick={() => handleSwipe('no')}
          disabled={isAnimating}
          className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg active:scale-90 transition-all disabled:opacity-50"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF2D55" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <button
          onClick={() => handleSwipe('yes')}
          disabled={isAnimating}
          className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg active:scale-90 transition-all disabled:opacity-50"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#4CAF50" stroke="#4CAF50" strokeWidth="1">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </button>
      </div>

    </div>
  );
};

export default SwipeScreen;